/**
 * Typen und Abrufe fuer den UI-Neubau.
 *
 * Kosten der Endpunkte (Messung 12.09.2026) -- bestimmt, wann was geladen wird:
 *   /api/regime, /health, /api/status, /api/ideas/options   < 0,3 s
 *   /api/top-ideas, /api/ideas/filter                       ~34 s ungecacht,
 *                                                           danach aus Redis
 */
import { apiFetch } from '../lib/api'
import type { LensKey } from '../lib/lenses'

export interface Sizing {
  vola_1j_pct: number | null
  vola_3j_pct: number | null
  max_drawdown_pct: number | null
  historie_tage: number | null
  verlustannahme_pct: number | null
  verlustannahme_quelle: string | null
  risikobudget_pct: number
  max_position_pct: number | null
  an_obergrenze: boolean
  belastbar: boolean
  hinweise: string[]
}

interface IdeaBasis {
  symbol: string
  name: string
  sector: string | null
  market_cap: number | null
  conviction: number
  lenses_judged: number
  lenses_total: number
  lenses_selected: string[] | null
  avg_score: number
  sizing?: Sizing
  gates?: Record<string, { bestanden: boolean | null; begruendung: string }>
  /** Potenzial bis zum fairen Wert je Basis (vorgewaermt, s. fairwert_liste.py). */
  fairwert?: Partial<Record<FairBasis, { potenzial_pct: number; ausserhalb: boolean }>>
}

/** Die Basen der Fair-Wert-Liste -- dieselben Linien wie in der Kursgrafik. */
export type FairBasis = 'kgv' | 'kuv' | 'dividende'

export interface FairEintrag {
  /** (fairer Wert / Kurs - 1) * 100 -- so weit muesste der Kurs steigen. */
  potenzial_pct: number
  /** (Kurs / fairer Wert - 1) * 100 -- dieselbe Lage vom Kurs aus gesehen. */
  abstand_pct: number
  fair: number
  vielfaches: number | null
  /** Nur bei der Dividende: die Ø-Rendite in Prozent. */
  rendite_pct: number | null
  takt: string | null
  ausserhalb: boolean
}

export interface UnterbewertetTitel {
  symbol: string
  name: string
  sector: string | null
  market_cap: number | null
  kurs: number
  kurs_stand: string
  quality: number | null
  quality_coverage: number
  datenhinweise: string[]
  basen: Partial<Record<FairBasis, FairEintrag>>
  potenzial_mittel_pct: number
}

export interface UnterbewertetAnfrage {
  basen: FairBasis[]
  alle: boolean
  min_potenzial: number
  min_quality: number | null
}

export interface UnterbewertetAntwort {
  titel: UnterbewertetTitel[]
  gesamt: number
  begrenzt_auf: number | null
  bestand?: number
  berechnet_am: string | null
  stand: string | null
  wird_berechnet: boolean
}

type LinsenPunkte = { [K in LensKey]: number }
type LinsenUrteile = { [K in LensKey as `${K}_verdict`]: string }
type LinsenAbdeckung = { [K in LensKey as `${K}_coverage`]: number }

export type Idea = IdeaBasis & LinsenPunkte & LinsenUrteile & LinsenAbdeckung

export function urteilVon(idea: Idea, key: LensKey): { punkte: number; urteil: string; abdeckung: number } {
  return {
    punkte: idea[key],
    urteil: idea[`${key}_verdict` as const],
    abdeckung: idea[`${key}_coverage` as const],
  }
}

export interface RegimeResponse {
  snapshot: { date?: string | null }
  regime: {
    score: number
    label: string
    color: string
    score_0_100: number
    position_size_factor: number
    coverage: number
    missing: string[]
    components: Record<string, number>
  }
  source?: string
  age_days?: number
  /** Wie lange die laufende Phase schon haelt und wie lange solche Phasen hielten. */
  phasen?: {
    label: string
    seit: string
    tage: number
    median_tage: number | null
    laengste_tage: number | null
    abgeschlossene_phasen: number
    wechsel_gesamt: number
  }
  /** Stand gegen Vortag, Vorwoche, Vormonat -- eine Zahl allein zeigt keine Bewegung. */
  verlauf?: {
    heute: number
    vortag: Verlaufspunkt | null
    vorwoche: Verlaufspunkt | null
    vormonat: Verlaufspunkt | null
  }
  /** Wie oft ein Rueckschlag normal ist -- Klima-Auskunft, keine Faelligkeit. */
  rhythmus?: {
    jahre_historie: number
    korrektur: Rueckschlag | null
    baerenmarkt: Rueckschlag | null
  }
  /** Marktbreite und die zweistufige Warnung. */
  stimmung?: {
    stand: string
    anteil: number
    band: string
    erklaerung: string
    rang_pct: number
    mittel_30: number
    mittel_250: number
    abstand_hoch_pct: number
    tempo_pct: number | null
    stufe: 0 | 1 | 2
    warnung_seit: string | null
    bilanz: Record<string, { episoden: number; treffer: number; gemessen: string }>
  }
}

export interface Verlaufspunkt {
  datum: string
  wert: number
  diff: number
}

export interface Rueckschlag {
  episoden: number
  alle_x_jahre: number
  letzte_begann: string
  her_jahre: number
}

export interface Datenquelle {
  stand: string | null
  tage: number | null
  veraltet: boolean
}

export interface HealthResponse {
  status: string
  timestamp: string
  checks?: Record<string, boolean>
  daten?: {
    kurse_tage_alt: number | null
    fundamentaldaten_tage_alt: number | null
    veraltet: boolean
    hinweis: string | null
  }
}

export interface StatusResponse {
  /** Je Quelle, wann zuletzt geholt wurde -- Kurse taeglich, der Rest nicht. */
  datenstand?: {
    kurse: Datenquelle
    abschluesse: Datenquelle
    profile: Datenquelle
    hinweis: string | null
  }
  universum: number | null
  vergleichsgruppe: number | null
  kurse_stand: string | null
  kurse_tage_alt: number | null
  kurse_veraltet: boolean
  rueckvergleich: { stichtage: number; bis: string | null } | null
  stand: string
}

export interface IdeasOptions {
  lenses: { name: string; angeschlossen: boolean; in_asymmetric_alpha: boolean }[]
  gates: { name: string; beschreibung: string }[]
  presets: Record<string, { lenses: string[]; gates: string[]; beschreibung: string }>
  risikobudgets: { name: string; pct: number; standard: boolean }[]
  position_obergrenze_pct: number
}

/** Der Screener schickt standardmaessig genau {sizing: true, limit: 100} -- s. cache_util.py. */
export interface FilterRequest {
  lenses: LensKey[] | null
  gates: string[] | null
  sizing: boolean
  limit: number
}

export interface FilterResponse {
  count: number
  ideas: Idea[]
  berechnet_am?: string | null
  quelle?: string
  /** Titel vor der Kuerzung durch die Bezahlschranke (tarife.py). */
  gesamt?: number
  /** Kostenlose Konten: auf so viele gekuerzt; null bei Pro. */
  begrenzt_auf?: number | null
}

export interface WatchlistItem {
  symbol: string
  name: string
  added_at?: string
}

export interface Watchlist {
  id: string
  name: string
  items: WatchlistItem[]
}

export interface Job {
  schluessel: string
  titel: string
  zweck: string
  braucht_abo: boolean
  betriebsart: string
  dauer_minuten: number
  argumente: string[]
  ausloesbar: boolean
}

export interface JobsResponse {
  abo_aktiv: boolean
  jobs: Job[]
}

export interface TriggerResponse {
  status: string
  import_type: string
  titel?: string
  message: string
  job_id: string
}

export interface ApiInfo {
  name: string
  version: string
  environment: string
  paid_api_enabled: boolean
  stats: { tickers: number; users: number }
}

/**
 * Oeffentlicher Ausschnitt der Top-Ideen fuer Besucher (13.09.2026): drei
 * Titel ohne Begruendungen je Linse. `wird_berechnet` heisst, das Backend
 * rechnet gerade neu -- bis dahin ist die Liste leer.
 */
export interface TeaserResponse {
  ideas: Idea[]
  berechnet_am: string | null
  wird_berechnet: boolean
}

/** Treffer der Suche nach Kuerzel, Name oder ISIN (/api/suche). */
export interface SuchTreffer {
  symbol: string
  name: string
  boerse: string | null
  land: string | null
  isin: string | null
}

/** Punkteregeln der Linsen (/api/linsen/regeln), im Backend gegen die Linsen getestet. */
export interface RegelKriterium {
  schluessel: string
  name: string
  max_punkte: number
  einheit: string
  skala: number
  regel: string
  erlaeuterung: string | null
}

export interface LinsenRegeln {
  linsen: Record<LensKey, RegelKriterium[]>
  urteile: { urteil: string; ab_punkten: number }[]
}

export interface AktieKriterium {
  schluessel: string
  name: string
  einheit: string
  skala: number
  wert: number | null
  /** Statt einer Zahl: "ja", "nein", "Verlust oder kein Gewinnwachstum" */
  wert_text: string | null
  punkte: number | null
  max_punkte: number
  regel: string
  fehlt: boolean
}

export interface AktieLinse {
  punkte: number
  urteil: string
  abdeckung: number
  kriterien: AktieKriterium[]
  details?: {
    epv_je_aktie: number | null
    kapitalkosten_pct: number | null
    risikoklasse: string | null
    kapitalrueckfluss_pct: number | null
    fehlt_grund: string | null
  }
}

/** Eine Zeile aus dem Abschluss, Betraege in Berichtswaehrung. */
export interface Abschlusszeile {
  date: string
  reported_currency: string | null
  revenue: number | null
  cost_of_revenue: number | null
  gross_profit: number | null
  net_income: number | null
  total_assets: number | null
  cash_and_short_term: number | null
  total_current_assets: number | null
  total_current_liabilities: number | null
  long_term_debt: number | null
  total_equity: number | null
  operating_cash_flow: number | null
  free_cash_flow: number | null
  capex: number | null
}

export interface Geschaeftsjahr extends Abschlusszeile {
  umsatzwachstum_pct: number | null
  kostenquote_pct: number | null
  bruttomarge_pct: number | null
  nettomarge_pct: number | null
  fcf_marge_pct: number | null
  eigenkapitalquote_pct: number | null
  liquiditaet_3: number | null
  investitionen: number | null
}

export interface Zwischenbericht extends Abschlusszeile {
  name: string
  nettomarge_pct: number | null
  umsatz_zum_vorjahr_pct: number | null
  /** Der Umsatz passt nicht zur eigenen Reihe -- markiert, ohne Vorjahresvergleich. */
  unstimmig: boolean
}

export type Stufe = 'besucher' | 'kostenlos' | 'pro'

/**
 * Einzelansicht je Aktie (/api/aktie/{symbol}, 15.09.2026). Das Backend
 * kuerzt auf die Stufe: ohne Pro fehlen Begruendung, aeltere Jahre und
 * Zwischenberichte in der Antwort -- die Seite zeigt dort nur Attrappen.
 */
/** Eine Bitte um den Pro-Zugang, wie sie im Zugang erscheint. */
export interface ProAnfrage {
  id: string
  status: 'offen' | 'freigeschaltet' | 'abgelehnt'
  nachricht: string | null
  gestellt_am: string | null
  entschieden_am: string | null
  entschieden_von: string | null
  konto: { email: string | null; name: string | null; seit: string | null } | null
}

export interface Befund {
  symbol: string
  schwere: 'fehler' | 'hinweis'
  text: string
}

/** Stand des Datenwaechters: Steht die Anzeige auf dem letzten geprueften Stand? */
export interface DatenwaechterStand {
  gesperrt: boolean
  sperre: { seit: string | null; grund: string; quelle: string; befunde: Befund[] } | null
  pruefliste: {
    geprueft_am: string
    titel_geprueft: number
    titel_erwartet: number
    fehler: number
    hinweise: number
    befunde: Befund[]
    referenzen_vom: string | null
  } | null
  /** Bestand gegen die SEC (jobs/bestandspruefung.py); null, bis er einmal lief. */
  bestand?: BestandBericht | null
}

export interface BestandWert {
  verglichen: number
  gleich: number
  nah: number
  abweichend: number
  einheit: number
  gleich_pct: number | null
  nah_pct: number | null
  beispiele: { symbol: string; unser: number; sec: number; faktor: number | null }[]
}

export interface BestandBericht {
  geprueft_am: string
  jahr: number
  firmen: number
  titel: number
  zuordnung: { umbenannt: number; andere_firma: { symbol: string; unser: string; sec: string }[] }
  perioden: {
    jahre?: number
    jahre_fehlen?: number
    quartale?: number
    quartale_fehlen?: number
    firmen_mit_luecken: number
    groesste_luecken: { symbol: string; fehlen: number }[]
    ohne_zeile: number
  }
  werte: Record<string, BestandWert>
}

export interface AktieAntwort {
  stufe: Stufe
  titel: {
    symbol: string
    name: string
    boerse: string | null
    land: string | null
    sektor: string | null
    branche: string | null
    isin: string | null
    waehrung: string
  }
  kurs: { wert: number | null; stand: string | null }
  bewertung: {
    konviktion: number
    linsen_urteilend: number
    linsen_gesamt: number
    schnitt: number
    rang: number | null
    linsen: Record<LensKey, { punkte: number; urteil: string; abdeckung: number }>
  }
  risiko: Sizing
  kennzahlen: {
    marktwert_usd: number | null
    kgv: number | null
    kuv: number | null
    fcf_rendite_pct: number | null
    dividendenrendite_pct: number | null
    umsatzwachstum_pct: number | null
    nettomarge_pct: number | null
    eigenkapitalquote_pct: number | null
    verlust: boolean
    geschaeftsjahr_ende: string | null
    berichtswaehrung: string | null
    fx_rate: number | null
  }
  hinweise: { schluessel: string; text: string }[]
  /** [Datum, Schlusskurs], aeltester zuerst, split- und sprungbereinigt */
  kursreihe: [string, number][]
  dividenden: { datum: string; betrag: number; rhythmus: string | null; angekuendigt: boolean }[]
  naechste_zahlen: string | null
  /** Woher der Termin kommt: Datenabo oder geschaetzt aus den SEC-Meldungen des Vorjahres. */
  naechste_zahlen_termin?: { datum: string | null; geschaetzt: boolean; vorjahr: string | null }
  /** Marken unter der Kursgrafik: Periodenende, 'Q3' bzw. 'H1', Zeitraum. */
  quartalsmarken: { datum: string; kurz: string; periode: string }[]
  /** Fair-Wert-Linien je Kennzahl; `punkte` ist die Kurve im Grafikfenster. */
  fairwerte: {
    schluessel: string
    name: string
    vielfaches: number | null
    wert: number | null
    jahre: number
    tage: number
    punkte: [string, number][]
    stuetzpunkte: string[]
    /** Das Vielfache selbst im Zeitverlauf -- bewegt sich taeglich mit dem Kurs. */
    verlauf: [string, number][]
    /** 'quartal' oder 'jahr' -- wie fein die Stuetzpunkte sitzen. */
    takt: string
    grund: string | null
    /** Vielfaches ausserhalb der üblichen Spanne: zeigen, aber nie von selbst. */
    ausserhalb: boolean
    /** Nur bei der Dividende: die Ø-Rendite in Prozent (`vielfaches` ist dort 1 / Rendite). */
    rendite_pct?: number | null
  }[]
  epv_je_aktie: number | null
  /** Median-KGV der vergleichbaren Jahre und der Wert, der sich daraus fuer heute ergibt */
  durchschnitts_kgv: { kgv: number | null; wert: number | null; jahre: number; grund: string | null }
  begruendung: Record<LensKey, AktieLinse> | null
  zahlen: {
    /** neuestes Geschaeftsjahr zuerst */
    jahre: Geschaeftsjahr[]
    zwischenberichte: { takt_monate: number | null; perioden: Zwischenbericht[] } | null
    waehrungsgrenze: string | null
  }
}

/** Ideen und Systeminfos verlangen seit 13.09.2026 eine Anmeldung (`auth: true`). */
export const QualyzApi = {
  regime: () => apiFetch<RegimeResponse>('/api/regime'),
  health: () => apiFetch<HealthResponse>('/health'),
  status: () => apiFetch<StatusResponse>('/api/status'),
  info: () => apiFetch<ApiInfo>('/api/info', { auth: true }),
  teaser: () => apiFetch<TeaserResponse>('/api/teaser'),
  aktie: (symbol: string) =>
    apiFetch<AktieAntwort>(`/api/aktie/${encodeURIComponent(symbol)}`, { auth: 'optional' }),
  linsenRegeln: () => apiFetch<LinsenRegeln>('/api/linsen/regeln'),
  suche: (eingabe: string) => apiFetch<SuchTreffer[]>(`/api/suche?q=${encodeURIComponent(eingabe)}`),
  topIdeas: (limit = 20) => apiFetch<Idea[]>(`/api/top-ideas?limit=${limit}`, { auth: true }),
  filterIdeas: (req: FilterRequest) =>
    apiFetch<FilterResponse>('/api/ideas/filter', { method: 'POST', body: req, auth: true }),
  ideasOptions: () => apiFetch<IdeasOptions>('/api/ideas/options'),
  unterbewertet: (a: UnterbewertetAnfrage) => {
    const q = new URLSearchParams({ basen: a.basen.join(','), alle: String(a.alle), min_potenzial: String(a.min_potenzial) })
    if (a.min_quality !== null) q.set('min_quality', String(a.min_quality))
    return apiFetch<UnterbewertetAntwort>(`/api/unterbewertet?${q}`, { auth: true })
  },
  watchlist: () => apiFetch<Watchlist>('/api/watchlist', { auth: true }),
  watchlistAdd: (symbol: string) =>
    apiFetch<Watchlist>('/api/watchlist/items', { method: 'POST', body: { symbol }, auth: true }),
  watchlistRemove: (symbol: string) =>
    apiFetch<Watchlist>(`/api/watchlist/items/${encodeURIComponent(symbol)}`, { method: 'DELETE', auth: true }),
  jobs: () => apiFetch<JobsResponse>('/api/admin/jobs', { auth: true }),
  datenwaechter: () => apiFetch<DatenwaechterStand>('/api/admin/datenwaechter', { auth: true }),
  proAnfrageStellen: (nachricht?: string) =>
    apiFetch<{ anfrage: ProAnfrage; neu: boolean; gemeldet: boolean }>('/api/pro/anfrage', {
      method: 'POST', body: { nachricht: nachricht ?? null }, auth: true }),
  proAnfrageEigene: () =>
    apiFetch<{ anfrage: ProAnfrage | null }>('/api/pro/anfrage', { auth: true }),
  proAnfragen: (status?: string) =>
    apiFetch<{ anfragen: ProAnfrage[]; offen: number }>(
      `/api/admin/pro-anfragen${status ? `?status=${status}` : ''}`, { auth: true }),
  proAnfrageEntscheiden: (id: string, freischalten: boolean) =>
    apiFetch<{ anfrage: ProAnfrage; geaendert: boolean; hinweis: string | null }>(
      `/api/admin/pro-anfragen/${encodeURIComponent(id)}/entscheiden`, {
        method: 'POST', body: { token: '', freischalten }, auth: true }),
  proFreigabeAnsehen: (token: string) =>
    apiFetch<{ anfrage: ProAnfrage }>(`/api/pro-freigabe/${encodeURIComponent(token)}`),
  proFreigabeEntscheiden: (token: string, freischalten: boolean) =>
    apiFetch<{ anfrage: ProAnfrage; geaendert: boolean; hinweis: string | null }>(
      '/api/pro-freigabe', { method: 'POST', body: { token, freischalten } }),
  passwortVergessen: (email: string) =>
    apiFetch<{ ok: boolean; hinweis: string }>('/api/auth/passwort-vergessen', {
      method: 'POST', body: { email } }),
  passwortNeuPruefen: (token: string) =>
    apiFetch<{ email: string }>(`/api/auth/passwort-neu/${encodeURIComponent(token)}`),
  passwortNeuSetzen: (token: string, passwort: string) =>
    apiFetch<{ ok: boolean; email: string }>('/api/auth/passwort-neu', {
      method: 'POST', body: { token, passwort } }),
  passwortAendern: (altes: string, neues: string) =>
    apiFetch<{ ok: boolean; access_token: string }>('/api/auth/passwort-aendern', {
      method: 'POST', body: { altes, neues }, auth: true }),
  datenwaechterFreigeben: () =>
    apiFetch<{ freigegeben: boolean; vorwaermen: string | null }>(
      '/api/admin/datenwaechter/freigeben', { method: 'POST', auth: true }),
  triggerJob: (schluessel: string) =>
    apiFetch<TriggerResponse>(`/api/admin/import/trigger/${encodeURIComponent(schluessel)}`, {
      method: 'POST',
      auth: true,
    }),
}
